"use client";
import { useRouter } from "next/navigation";
import { Room, RoomEvent } from "livekit-client";
import { useAppSelector } from "@client/store/store.hooks";
import { HostLeaveOption } from "@shared/constants/constant";
import { useState, useRef, useCallback, useEffect } from "react";
import { leaveMeeting } from "@client/actions/meeting/meeting.actions";
import { stopRecording } from "@client/actions/recording/recording.actions";
import { Logger } from "@shared/utils/logger";

interface UseLeaveHandlerOptions {
  room: Room;
  isRecording?: boolean;
  onStopRecording?: () => Promise<void>;
  onBeforeMeetingEnd?: () => Promise<void>;
  onIntentionalLeave?: () => void;
}

interface UseLeaveHandlerReturn {
  /** Leave meeting (for regular users) */
  handleLeave: () => Promise<void>;
  /** Leave with specific option (for host) */
  handleHostLeave: (option: HostLeaveOption) => Promise<void>;
  /** Show leave modal (for host) */
  showLeaveModal: boolean;
  /** Open leave modal */
  openLeaveModal: () => void;
  /** Close leave modal */
  closeLeaveModal: () => void;
  /** Loading state */
  isLeaving: boolean;
  /** Currently loading leave option (for showing correct spinner) */
  loadingOption: HostLeaveOption | null;
  /** Participant count (excluding self) */
  participantCount: number;
}

/**
 * Handles meeting leave logic for both host and regular users.
 * - Host: Shows modal with options ("End for All" or "Leave Only").
 * - Regular User: Directly disconnects.
 */
export const useLeaveHandler = ({
  room,
  isRecording = false,
  onStopRecording,
  onBeforeMeetingEnd,
  onIntentionalLeave
}: UseLeaveHandlerOptions): UseLeaveHandlerReturn => {
  const router = useRouter();
  const [isLeaving, setIsLeaving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [loadingOption, setLoadingOption] = useState<HostLeaveOption | null>(null);

  // Use refs to avoid recreating callback when props change
  const roomNameRef = useRef(room.name);
  roomNameRef.current = room.name;

  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  const onStopRecordingRef = useRef(onStopRecording);
  onStopRecordingRef.current = onStopRecording;

  // Get connection details for host info
  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const isHost = connectionDetails?.isHost ?? false;
  const canModerate = connectionDetails?.canModerate ?? isHost;
  const hostId = connectionDetails?.hostId ?? 0;
  const hostName = connectionDetails?.hostName ?? "";
  const userId = connectionDetails?.userId;
  const guestId = connectionDetails?.guestId;

  // Calculate participant count
  // Calculate participant count with reactive updates
  const [participantCount, setParticipantCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      if (!room) return;

      // Filter participants similar to MeetContent
      const others = Array.from(room.remoteParticipants.values()).filter((p) => {
        // Exclude waiting room participants (lobby tokens have canPublish=false)
        if (p.permissions && p.permissions.canPublish === false) return false;
        // Optionally filter recorders if identified (e.g., by name or metadata) in future
        return true;
      });

      setParticipantCount(others.length);
    };

    updateCount();

    // Subscribe to participant changes
    // Subscribe to participant changes
    const events = [
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.ParticipantPermissionsChanged
    ];
    events.forEach((evt) => room.on(evt, updateCount));

    return () => {
      events.forEach((evt) => room.off(evt, updateCount));
    };
  }, [room]);

  const openLeaveModal = useCallback(() => {
    setShowLeaveModal(true);
  }, []);

  const closeLeaveModal = useCallback(() => {
    if (!isLeaving) {
      setShowLeaveModal(false);
    }
  }, [isLeaving]);

  const stopRecordingBeforeLeave = useCallback(async (): Promise<boolean> => {
    if (!isHost || !isRecordingRef.current) {
      return true;
    }

    try {
      if (onStopRecordingRef.current) {
        await onStopRecordingRef.current();
        return true;
      }

      if (hostId && roomNameRef.current) {
        const response = await stopRecording({
          hostId: Number(hostId),
          roomName: roomNameRef.current
        });

        if (response.status) {
          return true;
        } else {
          Logger.warn("useLeaveHandler", "Failed to stop recording:", response.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      Logger.error("useLeaveHandler", "Error stopping recording before leave:", error);
      return false;
    }
  }, [isHost, hostId]);

  /**
   * Disconnects and navigates to the end screen.
   * Navigates *before* disconnecting to prevent "Connection Lost" overlay.
   */
  const performDisconnect = useCallback(
    (isEnded: boolean = false, apiData?: { participantId?: string; roomId?: string; totalTime?: number }) => {
      // Signal intentional leave to suppress connection error
      if (onIntentionalLeave) {
        onIntentionalLeave();
      }

      // Navigate first to start unmounting the component
      // This prevents the "Connection Lost" overlay from appearing
      const roomName = room.name;
      // Capture current hash (encryption key) for E2EE meetings
      const hash = typeof window !== "undefined" ? window.location.hash : "";

      // Prefer API data, fallback to connection details
      const participantId = apiData?.participantId || connectionDetails?.participantId || "";
      const totalTime = apiData?.totalTime;
      const odooOccurrenceId = connectionDetails?.odooOccurrenceId;

      const baseUrl = `/meeting-ended?room=${encodeURIComponent(roomName)}`;
      let url = isEnded ? `${baseUrl}&ended=true` : baseUrl;

      if (isEnded && (isHost || canModerate)) {
        url += "&canReopen=true";
      }

      // Add participantId for review functionality
      if (participantId) {
        url += `&participantId=${encodeURIComponent(participantId)}`;
      }

      if (odooOccurrenceId !== undefined && odooOccurrenceId !== null) {
        url += `&odooOccurrenceId=${encodeURIComponent(odooOccurrenceId)}`;
      }

      // Add totalTime to avoid additional API call
      if (totalTime !== undefined && odooOccurrenceId == null) {
        url += `&totalTime=${totalTime}`;
      }

      if (hash) {
        url += `&ref=${encodeURIComponent(hash)}`;
      }

      router.push(url);

      // Then disconnect the room (cleanup)
      try {
        room.disconnect();
      } catch (e) {
        Logger.warn("useLeaveHandler", "Error disconnecting room:", e);
      }
    },
    [
      room,
      router,
      onIntentionalLeave,
      connectionDetails?.participantId,
      connectionDetails?.odooOccurrenceId,
      isHost,
      canModerate
    ]
  );

  /**
   * Host leave handler with options.
   * Executes stop recording, API call, and disconnection sequence.
   */
  const handleHostLeave = useCallback(
    async (option: HostLeaveOption) => {
      if (isLeaving) {
        Logger.warn("useLeaveHandler", "Already leaving, ignoring duplicate call");
        return;
      }

      setIsLeaving(true);
      setLoadingOption(option);

      try {
        if (option === HostLeaveOption.END_FOR_ALL && onBeforeMeetingEnd) {
          await onBeforeMeetingEnd();
        }
        // Stop recording first if needed
        await stopRecordingBeforeLeave();

        // Call leave API and get participant data
        const response = await leaveMeeting({
          roomName: roomNameRef.current,
          hostId: hostId,
          isHost: isHost,
          leaveOption: option,
          hostName: hostName,
          userId: userId,
          guestId: guestId
        });

        // Close modal
        setShowLeaveModal(false);

        // Small delay for END_FOR_ALL to allow broadcast to complete
        if (option === HostLeaveOption.END_FOR_ALL) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        // Disconnect and navigate with participant data
        performDisconnect(option === HostLeaveOption.END_FOR_ALL, response.data);
      } catch (error) {
        Logger.error("useLeaveHandler", "Error during host leave:", error);
        performDisconnect();
      }
    },
    [
      isLeaving,
      onBeforeMeetingEnd,
      stopRecordingBeforeLeave,
      hostId,
      hostName,
      performDisconnect,
      isHost,
      userId,
      guestId
    ]
  );

  const handleLeave = useCallback(async () => {
    if (isLeaving) {
      Logger.warn("useLeaveHandler", "Already leaving, ignoring duplicate call");
      return;
    }

    // Show leave modal for everyone
    openLeaveModal();
  }, [isLeaving, openLeaveModal]);

  /**
   * Handle page unload/refresh.
   * Ensures user is marked as left even if they close tab or reload (beacon).
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!roomNameRef.current) return;

      const userId = connectionDetails?.userId;
      const isHostVal = connectionDetails?.isHost ?? false;
      const hostIdVal = connectionDetails?.hostId ?? 0;

      // Host should not trigger leave on reload
      if (isHostVal) return;

      const payload = {
        roomName: roomNameRef.current,
        hostId: hostIdVal,
        isHost: isHostVal,
        // Default to LEAVE_ONLY for host on reload to avoid accidental "End All"
        leaveOption: HostLeaveOption.LEAVE_ONLY,
        userId
      };

      // Use fetch with keepalive: true for reliable "fire and forget" on unload
      fetch("/api/meeting/leave", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch((e) => console.error("Failed to send leave beacon:", e));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [connectionDetails]);

  return {
    handleLeave,
    handleHostLeave,
    showLeaveModal,
    openLeaveModal,
    closeLeaveModal,
    isLeaving,
    loadingOption,
    participantCount
  };
};
